import dayjs from 'dayjs';
import { DateValue } from '../models/Models';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

export const getYearlyDaysFromDate = (yearly_days: DateValue[]) => {
  return yearly_days?.length ? yearly_days.map((x: DateValue) => dayjs(x.date).format('YYYY-MM-DD')) : [];
};

export const getYearlyDays = (yearly_days: number[]) => {
  return yearly_days?.length ? yearly_days.map((x: number) => dayjs(x).format('YYYY-MM-DD')) : [];
};

export function compare_array_dates(a: any, b: any) {
  return a.date - b.date;
}

export function compare_name_asc(a: any, b: any) {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
}

export function compare_name_desc(a: any, b: any) {
  if (a.name > b.name) {
    return -1;
  }
  if (a.name < b.name) {
    return 1;
  }
  return 0;
}

interface ExportCSVProps {
  header: string[];
  data: {
    dates?: any[];
    rawDates?: any[];
    values?: any[];
  };
  fileName?: string;
  dateFormat?: string;
  unit?: string;
  isSoilingSensorData?: boolean;
  sensorType?: string;
}

export const ExportCSV = ({ header, data, fileName, dateFormat, unit, isSoilingSensorData, sensorType }: ExportCSVProps) => {
  const downloadCSV = () => {
    // For soiling sensor data, download the appropriate template based on sensor type
    if (isSoilingSensorData) {
      // Map sensor type to file name
      const sensorTypeMap: { [key: string]: { file: string; name: string } } = {
        rde300: { file: 'rde300.csv', name: 'rde300_template.csv' },
        dustiq: { file: 'dustiq.csv', name: 'dustiq_template.csv' },
        soiling_ratio: { file: 'soiling ratio.csv', name: 'soiling_ratio_template.csv' },
      };

      // Default to rde300 if sensor type is not provided or not recognized
      const templateInfo = sensorTypeMap[sensorType || 'rde300'] || sensorTypeMap['rde300'];

      // Create a download link for the appropriate CSV template file
      const link = document.createElement('a');
      link.href = `/files/${templateInfo.file}`;
      link.download = templateInfo.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Format dates if dateFormat is provided
    let formattedDates = data?.dates;
    if (dateFormat && data?.rawDates) {
      formattedDates = data.rawDates.map((date: string) => {
        const d = new Date(Date.parse(date));
        return dayjs(d).format(dateFormat);
      });
    }

    // Original single-column logic
    // Add unit to the header if provided
    const customHeader = header.map((h: string) => (h === 'values' && unit ? `values (${unit})` : h));
    // Convert the data array into a CSV string
    const csvString = [
      customHeader, // Specify your headers here
      ...(formattedDates?.map((_item, i) => [formattedDates?.[i], data.values?.[i]]) || []), // Map your data fields accordingly
    ]
      .map((row) => row.join(';'))
      .join('\n');

    // Create a Blob from the CSV string
    const blob = new Blob([csvString], { type: 'text/csv' });

    // Generate a download link and initiate the download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button variant="contained" onClick={downloadCSV} startIcon={<DownloadIcon />}>
      Download CSV template
    </Button>
  );
};

export const getSolutionDisplayName = (cleaningPackages: any, technicalName: string) => {
  if (!technicalName || !cleaningPackages[technicalName]) {
    return technicalName || 'N/A';
  }
  return cleaningPackages[technicalName].name || technicalName;
};

export const isEmpty = (obj: object) => obj === null || Object.keys(obj).length === 0;

export const getPackageImageMapping = () => {
  return {
    'Manual, Dry': 'Manual_dry',
    'Manual, Wet': 'Manual_wet',
    'Mazaka, Dry': 'Mazaka_dry',
    'Mazaka, Wet': 'Mazaka_wet',
    'SCM, Wet': 'SCM_wet',
    'SunBrush, Dry': 'SunBrush_dry',
    'SunBrush, Wet': 'SunBrush_wet',
    'Sunpure PR300': 'Sunpure_PR300',
    'Solbright Gen7': 'Solbright_gen7',
    'Solar-lit auto': 'Solar_Lit_auto',
    'Solabot SB-01': 'Solabot_SB_01',
    'Sunpure PS100': 'Sunpure_PS100',
    'Solbright Semi-auto': 'Solbright_semi_auto',
    'Solabot SB-03': 'Solabot_SB_03',
    'BPMM, Wet': 'BPMM_wet',
    'BPMM, Dry': 'BPMM_dry',
    // Default fallback
    default: 'BPMM_wet',
  };
};

export const getImageKeyForPackage = (packageName: string): string => {
  const imageMapping = getPackageImageMapping();

  // Check if the package name contains any of the known package names
  for (const [packageKey, imageKey] of Object.entries(imageMapping)) {
    if (packageKey !== 'default' && packageName.includes(packageKey)) {
      return imageKey;
    }
  }

  // Return default if no match found
  return imageMapping['default'];
};

// Validation regex for number input (allow negative numbers and decimal numbers)
export const NUMBER_VALIDATION_REGEX = /^[-]?[0-9]+\.?[0-9]*$|^[-]?[0-9]*$/;

export const isValidNumberInput = (value: string): boolean => {
  return value === '' || NUMBER_VALIDATION_REGEX.test(value);
};

/**
 * Build standardized parameters for the soiling-ratios endpoint
 * This ensures consistent cache keys between CallContentField and SoilingRatioMultiSelect
 */
export const buildSoilingRatiosParams = (formData: any, getFormDataItem: any, projectId: string | undefined, role: string) => {
  const paramKeys = [
    'location',
    'rain_threshold',
    'tilt_angle',
    'surface_tilt',
    'variable_tilt_angle',
    'rain.rain_efficiency',
    'rain.compute_rain_efficiency',
  ];

  const params = paramKeys.reduce((acc: any, key: string) => {
    const value = getFormDataItem(formData, key);
    const keyName = key.split('.').pop() || key;
    typeof value === 'object' ? Object.assign(acc, value) : Object.assign(acc, { [keyName]: value });
    return acc;
  }, {});

  // Add project_id
  if (projectId) {
    params.project_id = projectId;
  }

  // Add hsu parameter based on role
  params.hsu = role === 'operational_strategy' ? true : false;

  return params;
};

/**
 * Build standardized body for the soiling-ratios endpoint
 * This ensures consistent cache keys between CallContentField and SoilingRatioMultiSelect
 */
export const buildSoilingRatiosBody = (formData: any, getFormDataItem: any, patternKey: string = 'rain_pattern') => {
  // Get sensor files
  const getSensorFiles = () => {
    const sensorFiles: { uid: string; type: string }[] = [];
    const soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));

    soilingDataKeys.forEach((blockKey) => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      const sensorType = getFormDataItem(formData, `${blockKey}.type`) || 'rde300';
      if (fileUid) {
        sensorFiles.push({
          uid: fileUid,
          type: sensorType,
        });
      }
    });

    return sensorFiles;
  };

  const sensorFiles = getSensorFiles();
  const rainData = getFormDataItem(formData, 'rain2');

  // Build base body with user_patterns
  const baseBody = rainData?.from_api
    ? { user_patterns: { [patternKey + '_from_api']: rainData.from_api, [patternKey]: null } }
    : { user_patterns: { [patternKey + '_from_api']: rainData?.from_api, [patternKey]: rainData?.rain_pattern } };

  // Include plant and use_plant_geometry
  const plantData = getFormDataItem(formData, 'plant');
  const usePlantGeometry = getFormDataItem(formData, 'use_plant_geometry');

  // Return body with or without sensor_files
  return sensorFiles.length > 0
    ? { ...baseBody, sensor_files: sensorFiles, plant: plantData, use_plant_geometry: usePlantGeometry }
    : { ...baseBody, plant: plantData, use_plant_geometry: usePlantGeometry };
};
